import {
  Autocomplete,
  Avatar,
  Box,
  Chip,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { TeamRoleKey, User } from '@altyor-kronos/shared-types';
import type { TeamStepValues } from '../validators';
import { TEAM_SECTIONS } from './teamSections';
import { buildTeamOptions, isRecommendedForRole } from './teamOptions';
import { colors } from '../../../core/theme/theme';

interface StepTeamProps {
  team: TeamStepValues;
  users: Array<{ id: string; data: User }>;
  onChange: (key: TeamRoleKey, uids: string[]) => void;
  errors: { managers?: string };
}

function initials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

export default function StepTeam({ team, users, onChange, errors }: StepTeamProps) {
  const { t } = useTranslation();

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', width: '100%' }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        {t('projects.wizard.stepTeam', 'Build the team')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        {t('projects.wizard.teamHelp', 'Only Project Managers are required. All other roles are optional.')}
      </Typography>

      {TEAM_SECTIONS.map((section) => {
        const options = buildTeamOptions(users, section.filterRole);
        const selectedUids = team[section.key] ?? [];
        const selectedUsers = selectedUids
          .map((uid) => users.find((u) => u.id === uid))
          .filter(Boolean) as Array<{ id: string; data: User }>;

        return (
          <Box key={section.key} sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="subtitle2">
                {t(section.labelKey, section.key)}
              </Typography>
              {section.required && (
                <Chip label={t('common.required')} size="small" sx={{ bgcolor: colors.yellow, color: '#000', fontSize: 11, height: 20 }} />
              )}
            </Box>

            <Autocomplete
              multiple
              options={options}
              value={selectedUsers}
              onChange={(_e, newValue) => {
                onChange(section.key, newValue.map((u) => u.id));
              }}
              getOptionLabel={(opt) => opt.data.displayName}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              groupBy={
                section.filterRole === null
                  ? undefined
                  : (opt) =>
                      isRecommendedForRole(opt, section.filterRole)
                        ? t('projects.wizard.team.recommended', 'Recommended')
                        : t('projects.wizard.team.others', 'Others')
              }
              filterOptions={(opts, state) => {
                const q = state.inputValue.toLowerCase();
                if (!q) return opts;
                return opts.filter((o) =>
                  (o.data.displayName ?? '').toLowerCase().includes(q) ||
                  (o.data.jobTitle ?? '').toLowerCase().includes(q) ||
                  o.data.email.toLowerCase().includes(q),
                );
              }}
              renderOption={(props, option) => {
                const { key, ...rest } = props;
                return (
                  <Box component="li" key={key} {...rest} sx={{ display: 'flex', gap: 1.5, alignItems: 'center', py: 1 }}>
                    <Avatar
                      src={option.data.photoURL ?? undefined}
                      variant="square"
                      sx={{ width: 36, height: 36, fontSize: 14 }}
                    >
                      {initials(option.data.displayName)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {option.data.displayName}
                      </Typography>
                      {option.data.jobTitle && (
                        <Typography variant="caption" color="text.secondary">
                          {option.data.jobTitle}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              }}
              slotProps={{
                chip: { size: 'small' },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t('projects.wizard.userSearchPlaceholder', 'Search by name or job title...')}
                  error={section.key === 'managers' && Boolean(errors.managers)}
                  helperText={section.key === 'managers' ? errors.managers : undefined}
                  size="small"
                />
              )}
            />
          </Box>
        );
      })}
    </Box>
  );
}
